import { useState, useMemo } from "react";
import { Filter, Search, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * MultiSelectFilterPopover — canonical filter selector for Tables toolbar.
 * Renders "Label ▾" or "Label · N" pill; opens a popover with a searchable list
 * of checkboxes bound to real data.
 *
 * Recency: when the search field is empty, we only render the last `recentLimit`
 * options (assumed to be in insertion order — newest last). This keeps the
 * popover short + focused on things the merchant just touched. The user then
 * types to reach the full catalog.
 *
 * `searchOf(option)` returns the concatenated searchable string. Defaults to
 * `labelOf(option)`. For the "Bağlı Ürün" filter we pass `${id} ${name}` so
 * either SKU or product name matches.
 *
 * `renderOption(option)` optionally overrides how each row is drawn inside
 * the label wrapper. Used by "Bağlı Ürün" to render "SKU · name" two-line.
 */
export const MultiSelectFilterPopover = ({
    label, options, selected, onChange, keyOf, labelOf, emptyLabel, testId,
    searchOf, renderOption, recentLimit = 5,
}) => {
    const [query, setQuery] = useState("");
    const matches = searchOf || labelOf;
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            // No search → show only the last `recentLimit` items, newest first.
            return options.slice(-recentLimit).reverse();
        }
        return options.filter((o) => matches(o).toLowerCase().includes(q));
    }, [options, query, matches, recentLimit]);
    const isRecency = !query.trim() && options.length > recentLimit;
    const toggle = (k) => {
        const next = new Set(selected);
        if (next.has(k)) next.delete(k); else next.add(k);
        onChange([...next]);
    };
    const active = selected.length > 0;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-xs transition-colors ${active ? "border-foreground/40 bg-muted/60 text-foreground" : "border-border bg-surface text-muted-foreground hover:text-foreground"}`}
                    data-testid={testId}
                >
                    <Filter className="h-3 w-3" />
                    <span>{label}</span>
                    {active && <span className="rounded-md bg-brand text-brand-fg px-1.5 py-0.5 text-[10px] font-mono">{selected.length}</span>}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0 overflow-hidden">
                <div className="p-2 border-b border-border">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ara…" className="h-8 pl-8 text-xs bg-background" data-testid={`${testId}-search`} />
                    </div>
                </div>
                {isRecency && (
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground/80" data-testid={`${testId}-recent-hint`}>
                        Son eklenen {Math.min(recentLimit, options.length)} · Daha fazlası için ara
                    </div>
                )}
                <div className="max-h-64 overflow-y-auto scroll-thin">
                    {visible.length === 0 ? (
                        <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">{emptyLabel || "Sonuç yok"}</div>
                    ) : visible.map((o) => {
                        const k = keyOf(o);
                        const checked = selected.includes(k);
                        return (
                            <label
                                key={k}
                                className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-muted/40 ${checked ? "bg-muted/30" : ""}`}
                                data-testid={`${testId}-option-${k}`}
                            >
                                <Checkbox checked={checked} onCheckedChange={() => toggle(k)} />
                                {renderOption ? renderOption(o) : <span className="truncate">{labelOf(o)}</span>}
                            </label>
                        );
                    })}
                </div>
                {active && (
                    <div className="border-t border-border p-2 bg-muted/20">
                        <button
                            onClick={() => onChange([])}
                            className="text-[11px] text-muted-foreground hover:text-foreground w-full text-left"
                            data-testid={`${testId}-clear`}
                        >
                            Temizle
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
